import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, LogOut, MailQuestion, ShieldCheck, Sparkles } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Logo } from "@/components/ui/Logo";
import { cn } from "@/lib/cn";
import { useAuth } from "@/stores/auth";
import { useAuthDialog } from "@/stores/authDialog";
import { userLabel } from "@/lib/userName";
import { useLogout } from "@/hooks/useAuthFlow";
import { fmtNum } from "@/lib/format";
import { SupportDialog, type SupportKind } from "@/components/dialogs/SupportDialog";
import { NAV_ITEMS, NAV_KEYS } from "./nav-items";

interface SidebarProps {
  onTopUp?: () => void;
}

export function Sidebar({ onTopUp }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // H4: logout - это серверный вызов /api/auth/logout (очищает HttpOnly
  // cookie). Локальный useAuth.logout срабатывает в onSettled хука и
  // чистит UI. После - редиректим на /, чтобы AppLayout не оставил
  // защищённую страницу с пустым user.
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const onLogout = () => {
    logoutMutation.mutate(undefined, {
      onSettled: () => navigate("/", { replace: true }),
    });
  };
  // Поддержка + предложить модель - open one dialog at a time.
  const [supportKind, setSupportKind] = useState<SupportKind | null>(null);
  const location = useLocation();
  const openAuthDialog = useAuthDialog((s) => s.openDialog);

  const balance = user?.balance ?? 0;
  const isGuest = !!user?.is_guest;

  return (
    <aside className="hidden lg:flex sticky top-0 h-[100dvh] w-[280px] shrink-0 flex-col border-r border-line-soft bg-surface/60 backdrop-blur">
      {/* Logo - кликом ведёт на главную (лендинг). */}
      <NavLink to="/" className="px-5 pt-5 pb-4 inline-block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
        <Logo size={32} subtitle={t("tagline")} />
      </NavLink>

      {/* Profile card - кликабельна, ведёт в настройки. */}
      {user && (
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="mx-3 mb-4 flex items-center gap-3 rounded-xl border border-line-soft bg-surface px-3 py-2.5 text-left hover:bg-surface-2 transition"
        >
          <Avatar initials={userLabel(user).slice(0, 2).toUpperCase()} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-ink">{userLabel(user)}</div>
            <div className="truncate text-xs text-ink-soft">{user.is_guest ? "Гостевой режим" : user.email}</div>
          </div>
          <ChevronDown className="h-4 w-4 text-ink-soft -rotate-90" />
        </button>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto no-scrollbar">
        {NAV_ITEMS.map(({ to, i18nKey, icon: Icon }) => {
          const active = location.pathname.startsWith(to);
          return (
            <NavLink
              key={to}
              to={to}
              className={cn(
                "flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-medium transition",
                active
                  ? "bg-brand-50 text-brand-700"
                  : "text-ink-mute hover:bg-surface-2 hover:text-ink",
              )}
            >
              <Icon className="h-[18px] w-[18px]" />
              {t(NAV_KEYS[i18nKey])}
            </NavLink>
          );
        })}
        {/* Админка - доступна только для role=admin. Визуально такая же,
            как и остальные NAV_ITEMS - серая, активная при path startsWith. */}
        {user?.role === "admin" && (
          <NavLink
            to="/admin"
            className={cn(
              "flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-medium transition",
              location.pathname.startsWith("/admin")
                ? "bg-brand-50 text-brand-700"
                : "text-ink-mute hover:bg-surface-2 hover:text-ink",
            )}
          >
            <ShieldCheck className="h-[18px] w-[18px]" />
            Админка
          </NavLink>
        )}
      </nav>

      {/* Credit usage - просто текущий баланс, без прогресс-бара и
          процента (нет смыслового «потолка», 30000 был визуальный якорь). */}
      <div className="m-3 rounded-2xl border border-line-soft bg-surface p-4">
        <div className="text-xs font-medium text-ink-mute">
          {isGuest ? "Гостевой режим" : t("sidebar.creditsUsed")}
        </div>
        <div className="mt-1.5 text-2xl font-semibold text-ink">
          {fmtNum(balance)} <span className="text-ink-soft text-sm font-normal">кредитов</span>
        </div>
        {isGuest ? (
          <>
            <Button className="mt-3 w-full" onClick={() => openAuthDialog("signup")}>
              Создать аккаунт
            </Button>
            <p className="mt-2 text-[11px] leading-snug text-ink-soft">
              В гостевом режиме пополнение недоступно. Зарегистрируйтесь, чтобы пополнять баланс.
            </p>
          </>
        ) : (
          <Button variant="secondary" className="mt-3 w-full" onClick={onTopUp}>
            {t("sidebar.topUp")}
          </Button>
        )}
      </div>

      {/* Support + suggest model - небольшие ссылки прямо над logout */}
      <div className="px-3 space-y-0.5">
        <Button variant="ghost" className="w-full justify-start gap-3 px-3 text-ink-soft" onClick={() => setSupportKind("suggest_model")}>
          <Sparkles className="h-[18px] w-[18px]" />
          Предложить модель
        </Button>
        <Button variant="ghost" className="w-full justify-start gap-3 px-3 text-ink-soft" onClick={() => setSupportKind("support")}>
          <MailQuestion className="h-[18px] w-[18px]" />
          Поддержка
        </Button>
      </div>

      {/* Logout */}
      <div className="px-3 pb-5 pt-2">
        <Button variant="ghost" className="w-full justify-start gap-3 px-3" onClick={onLogout}
                disabled={logoutMutation.isPending}>
          <LogOut className="h-[18px] w-[18px]" />
          {t("nav.logout")}
        </Button>
      </div>

      <SupportDialog
        open={supportKind !== null}
        kind={supportKind ?? "support"}
        onOpenChange={(o) => { if (!o) setSupportKind(null); }}
      />
    </aside>
  );
}
